import { useState, useEffect, useMemo, Fragment } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../../api/supabase'
import SearchableSelect from '../SearchableSelect'
import CustomerCombobox from '../CustomerCombobox'
import { StickyHScrollbar } from '../StickyHScrollbar'
import BillingBasisPanel from './BillingBasisPanel'
import { useStickyHScroll } from '../../hooks/useStickyHScroll'
import { useToast } from '../../hooks/useToast'
import InvoiceDetailModal from '../InvoiceDetailModal'
import type { Invoice } from '../../hooks/useInvoices'
import { computeInvoiceDueDate } from '../../lib/invoiceDueDate'
import { INVOICE_CATEGORIES, isDutyVat, type InvoiceCategory } from '../../lib/invoiceCategory'
import { errorMessage } from '../../lib/errorMessage'


interface ChargeCodeOption {
  id: string
  code: string
  description: string
  default_vat_rate: number
}

interface TaxRateOption {
  name: string
  rate: number
}

// Column names match job_charges exactly -- every save is an explicit allowlisted payload (see
// buildPayload), never a raw spread, so an embedded join object (sale_customer/cost_supplier/
// invoice, present after a load but never a real column) can't leak into an insert/update.
interface JobCharge {
  id: string
  job_id: string
  charge_code_id: string | null
  description: string | null
  notes: string | null
  sale_customer_id: string | null
  sale_quantity: number | null
  sale_rate: number | null
  sale_amount: number | null
  sale_currency: string
  sale_exch_rate: number | null
  sale_vat_rate: number | null
  sales_tax_code: string | null
  include_in_sale: boolean
  cost_supplier_id: string | null
  cost_quantity: number | null
  cost_rate: number | null
  est_amount: number | null
  act_amount: number | null
  cost_currency: string
  cost_exch_rate: number | null
  cost_vat_rate: number | null
  cost_tax_code: string | null
  supplier_ref: string | null
  supplier_invoice_received_at: string | null
  include_in_cost: boolean
  invoice_id: string | null
  est_profit: number | null
  act_profit: number | null
  sale_customer?: { id: string; name: string } | null
  cost_supplier?: { id: string; name: string } | null
  invoice?: { id: string; invoice_no: string; status: string } | null
}

interface ChargesTabProps {
  jobId: string
  defaultBillToId?: string | null
}

type ChargeForm = Partial<JobCharge>

// Zero Rated default -- Gary: most customers are overseas companies, so zero-rated is the
// common case, not standard-rated. Qty defaults to 1 on both sides -- a charge is almost always
// a single flat line, not a per-unit quantity.
const emptyForm = (defaultBillToId?: string | null): ChargeForm => ({
  sale_customer_id: defaultBillToId || null,
  sale_quantity: 1,
  sale_currency: 'GBP',
  sale_exch_rate: 1,
  sale_vat_rate: 0,
  sales_tax_code: 'Zero Rated',
  include_in_sale: true,
  cost_quantity: 1,
  cost_currency: 'GBP',
  cost_exch_rate: 1,
  cost_vat_rate: 0,
  cost_tax_code: 'Zero Rated',
  include_in_cost: true,
})

const fmt = (n: number | null | undefined) => (n ?? 0).toFixed(2)

// "Standard 20.00%" -> {name: "Standard", rate: 20}; "No VAT" (no % suffix) -> rate 0.
function parseTaxRates(rows: { source_value: string }[]): TaxRateOption[] {
  return rows.map(r => {
    const match = r.source_value.match(/^(.*?)\s*([\d.]+)%$/)
    return match ? { name: match[1].trim(), rate: parseFloat(match[2]) } : { name: r.source_value, rate: 0 }
  })
}

export default function ChargesTab({ jobId, defaultBillToId }: ChargesTabProps) {
  const { toast } = useToast()
  const [charges, setCharges] = useState<JobCharge[]>([])
  const [chargeCodes, setChargeCodes] = useState<ChargeCodeOption[]>([])
  // Z86: cost lines that have supplier-invoice allocations -- their actual cost is computed, not typed.
  const [invoicedChargeIds, setInvoicedChargeIds] = useState<Set<string>>(new Set())
  const [taxRates, setTaxRates] = useState<TaxRateOption[]>([])
  const [taxRatesStatus, setTaxRatesStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<ChargeForm>(emptyForm(defaultBillToId))
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Fields currently failing required-field validation -- drives the red border. Cleared as soon
  // as the field changes, not just on the next successful submit.
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set())

  const [selectedForInvoice, setSelectedForInvoice] = useState<Set<string>>(new Set())
  const [creatingInvoice, setCreatingInvoice] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [invoiceCategoryChoice, setInvoiceCategoryChoice] = useState<InvoiceCategory>('IMPORT')
  // charge_codes.is_duty_vat (20261001b). Fetched on its own and failure-tolerant so the Charges
  // tab keeps loading even before that migration has been run (then nothing is auto-inferred).
  const [dutyVatCodes, setDutyVatCodes] = useState<Map<string, string>>(new Map())
  const [categoryAutoReason, setCategoryAutoReason] = useState<string | null>(null)
  useEffect(() => {
    supabase.from('charge_codes').select('id, description').eq('is_duty_vat', true).then(({ data, error }) => {
      if (!error && data) setDutyVatCodes(new Map(data.map((r: { id: string; description: string }) => [r.id, r.description])))
    })
  }, [])
  const [invoiceNotesInput, setInvoiceNotesInput] = useState('')
  const [showInvoiceTypeDialog, setShowInvoiceTypeDialog] = useState(false)

  // In-place invoice viewing (Gary, 2026-09-28): "View Invoice" used to be a full-page jump to
  // /accounts, losing the Job page entirely -- this keeps the user on the job.
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null)
  const openInvoice = async (invoiceId: string) => {
    const { data } = await supabase
      .from('invoices')
      .select('*, customer:customers(id, code, name, contact_email), job:jobs(id, job_ref, client_ref)')
      .eq('id', invoiceId)
      .single()
    if (data) setViewingInvoice(data as Invoice)
  }
  const handleInvoiceStatusChange = async (invoiceId: string, status: string) => {
    await supabase.from('invoices').update({ status }).eq('id', invoiceId)
    await loadAll()
  }
  const handleInvoiceNotesChange = async (invoiceId: string, notes: string) => {
    const { error } = await supabase.from('invoices').update({ notes: notes || null }).eq('id', invoiceId)
    if (error) throw error
    await loadAll()
  }

  useEffect(() => { loadAll(); loadTaxRates() }, [jobId])

  // Split out from loadAll -- a tax-rate load failure (RLS gap, network blip) shouldn't block the
  // charges list/charge codes/customers from loading at all, and needs its own loading/error/empty
  // states since "loading" and "loaded but zero rows" are NOT the same thing (Gary, 2026-09-28:
  // the dropdown used to show "Loading…" forever for either case, indistinguishable, no retry).
  const loadTaxRates = async () => {
    setTaxRatesStatus('loading')
    try {
      // Real Sage tax rates (Settings > Financial Settings > Tax Rates), synced into
      // sage_reference_data by the Sage CustomerRef/tax-rate import -- not hardcoded, so a rate
      // change or a new rate added in Sage shows up here without a code change.
      const { data, error } = await supabase.from('sage_reference_data').select('source_value').eq('category', 'tax_rate').ilike('description', 'Imported from Sage%')
      if (error) throw error
      setTaxRates(parseTaxRates(data || []))
      setTaxRatesStatus('ready')
    } catch (err) {
      console.error('[ChargesTab] loadTaxRates failed', { jobId, err })
      Sentry.captureException(err instanceof Error ? err : new Error('Failed to load tax rates'), { extra: { jobId } })
      setTaxRatesStatus('error')
    }
  }

  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const [chargesRes, codesRes, allocRes] = await Promise.all([
        supabase
          .from('job_charges')
          .select('*, sale_customer:customers!sale_customer_id(id,name), cost_supplier:customers!cost_supplier_id(id,name), invoice:invoices(id,invoice_no,status)')
          .eq('job_id', jobId)
          .order('created_at', { ascending: true }),
        supabase.from('charge_codes').select('id, code, description, default_vat_rate').eq('is_active', true).order('code'),
        supabase.from('supplier_invoice_allocations').select('job_charge_id').eq('job_id', jobId),
      ])
      if (chargesRes.error) throw chargesRes.error
      if (codesRes.error) throw codesRes.error
      setCharges((chargesRes.data as any) || [])
      setChargeCodes(codesRes.data || [])
      // Failure-tolerant: a role without read access just sees the field as editable (the DB guard still protects it).
      setInvoicedChargeIds(new Set((allocRes.data || []).map((a: { job_charge_id: string }) => a.job_charge_id)))
    } catch (err) {
      const message = errorMessage(err, 'Failed to load charges')
      console.error('[ChargesTab] loadAll failed', { jobId, err })
      Sentry.captureException(err instanceof Error ? err : new Error(message), { extra: { jobId } })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const createChargeCode = async (description: string) => {
    try {
      let code = description.split(' ').slice(0, 3).map(w => w[0]).join('').toUpperCase().replace(/[^A-Z0-9]/g, '')
      if (!code) code = description.replace(/[^A-Z0-9]/g, '').substring(0, 4).toUpperCase()
      if (!code) throw new Error('Cannot generate valid code from description')

      const insertData = { code, description: description.trim(), default_vat_rate: 20, is_active: true }
      let insertResult = await supabase.from('charge_codes').insert([insertData]).select('id, code, description, default_vat_rate').single()

      if (insertResult.error) {
        const msg = (insertResult.error as any).message || ''
        if (msg.includes('unique') || msg.includes('duplicate')) {
          const suffix = Math.floor(Math.random() * 100).toString().padStart(2, '0')
          insertResult = await supabase.from('charge_codes').insert([{ ...insertData, code: `${code.substring(0, 2)}${suffix}` }]).select('id, code, description, default_vat_rate').single()
        }
      }
      if (insertResult.error) {
        const e = insertResult.error as any
        throw new Error(`${e.message || e.details || 'Unknown error'}${e.code ? ` (${e.code})` : ''}`)
      }
      const data = insertResult.data
      setChargeCodes(prev => [...prev, data])
      return { id: data.id, label: `${data.code} - ${data.description}` }
    } catch (err) {
      throw new Error(`Failed to create charge code: ${errorMessage(err, 'Unknown error')}`)
    }
  }

  const startEdit = (c: JobCharge) => {
    setEditingId(c.id)
    setForm({ ...c })
    setError(null)
    setFieldErrors(new Set())
  }

  // Once a charge is linked to an invoice, its Sales side is a committed customer promise --
  // only Void + re-issue (or a Credit Note) can change it. The Cost side must stay editable
  // indefinitely: the supplier invoice routinely arrives after we've already billed the
  // customer, and without this, act_cost could never be filled in and Job Profit (Act) would
  // stay a permanent estimate.
  const isInvoiced = (c: JobCharge) => !!c.invoice_id

  const cancelEdit = () => {
    setEditingId(null)
    setForm(emptyForm(defaultBillToId))
    setFieldErrors(new Set())
  }

  // Explicit allowlist -- never spread `form` raw into insert/update. After an edit, `form` also
  // carries the embedded sale_customer/cost_supplier/invoice join objects from the load, none of
  // which are real job_charges columns (this exact shape of bug is why the old tab was
  // silently deleting charges on every save -- see fix/job-charges-save-data-loss).
  // `locked` omits the Sales-side keys entirely (rather than sending them unchanged) so an
  // invoiced charge's update can never touch its committed Sales amounts, even defensively --
  // the edit form already renders them read-only, this is the second layer.
  const buildPayload = (locked: boolean) => ({
    job_id: jobId,
    charge_code_id: form.charge_code_id || null,
    ...(locked ? {} : {
      sale_customer_id: form.sale_customer_id || null,
      sale_quantity: form.sale_quantity ?? null,
      sale_rate: form.sale_rate ?? null,
      sale_amount: (form.sale_quantity || 0) * (form.sale_rate || 0),
      sale_currency: form.sale_currency || 'GBP',
      sale_exch_rate: form.sale_exch_rate ?? 1,
      sale_vat_rate: form.sale_vat_rate ?? 0,
      sales_tax_code: form.sales_tax_code || null,
      include_in_sale: form.include_in_sale ?? true,
      notes: form.notes || null,
    }),
    cost_supplier_id: form.cost_supplier_id || null,
    cost_quantity: form.cost_quantity ?? null,
    cost_rate: form.cost_rate ?? null,
    est_amount: (form.cost_quantity || 0) * (form.cost_rate || 0),
    act_amount: form.act_amount ?? null,
    cost_currency: form.cost_currency || 'GBP',
    cost_exch_rate: form.cost_exch_rate ?? 1,
    cost_vat_rate: form.cost_vat_rate ?? 0,
    cost_tax_code: form.cost_tax_code || null,
    supplier_ref: form.supplier_ref || null,
    supplier_invoice_received_at: form.supplier_invoice_received_at || null,
    include_in_cost: form.include_in_cost ?? true,
  })

  // Blocking: a field the row genuinely can't be saved without. Returns the set of invalid field
  // keys (drives the red border) -- empty means valid. Charge Code was already required before
  // this; Bill To (when Include in Sale is on) is new. Supplier is deliberately NOT required (Z86):
  // finance often invoices the sale side before the supplier/cost is known -- the list flags it instead.
  const validateRequired = (): Set<string> => {
    const invalid = new Set<string>()
    if (!form.charge_code_id) invalid.add('charge_code_id')
    if ((form.include_in_sale ?? true) && !form.sale_customer_id) invalid.add('sale_customer_id')
    return invalid
  }

  const saveCharge = async () => {
    const invalid = validateRequired()
    if (invalid.size > 0) {
      setFieldErrors(invalid)
      const messages = [
        invalid.has('charge_code_id') && 'Charge Code is required.',
        invalid.has('sale_customer_id') && 'Bill To is required when Include in Sale is checked.',
      ].filter(Boolean).join(' ')
      setError(messages)
      toast(messages, 'error')
      return
    }
    // Non-blocking: Gary's ask is "at least warn" for a zero/blank rate -- a real free-of-charge
    // line is legitimate, so this doesn't stop the save, just flags it's probably an oversight.
    if ((form.include_in_sale ?? true) && !form.sale_rate) toast('Sales Rate is 0 or blank -- saving anyway.', 'info')
    if ((form.include_in_cost ?? true) && !form.cost_rate) toast('Cost Est Rate is 0 or blank -- saving anyway.', 'info')

    setFieldErrors(new Set())
    setSaving(true)
    setError(null)
    try {
      const payload = buildPayload(!!form.invoice_id)
      // .select().single() -- confirm the row that's actually now in the DB, with its real id,
      // rather than trusting a 2xx status alone. If this doesn't come back, nothing is saved,
      // full stop, regardless of what the HTTP layer claims.
      const { data, error } = editingId
        ? await supabase.from('job_charges').update(payload).eq('id', editingId).select().single()
        : await supabase.from('job_charges').insert(payload).select().single()
      if (error) throw error
      if (!data) throw new Error('Save returned no row -- charge was not confirmed as saved.')
      cancelEdit()
      await loadAll()
      toast(editingId ? 'Charge updated.' : 'Charge added.', 'success')
    } catch (err) {
      const message = errorMessage(err, 'Failed to save charge')
      console.error('[ChargesTab] saveCharge failed', { jobId, editingId, form, err })
      Sentry.captureException(err instanceof Error ? err : new Error(message), { extra: { jobId, editingId, form } })
      setError(message)
      toast(`Failed to save charge: ${message}`, 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteCharge = async (id: string) => {
    const c = charges.find(x => x.id === id)
    if (c && isInvoiced(c)) {
      const msg = `Already invoiced (Invoice ${c.invoice?.invoice_no || ''}) -- void the invoice before deleting this charge.`
      setError(msg)
      toast(msg, 'error')
      return
    }
    // Z86: a cost line that supplier invoices have been allocated to can't be deleted (the allocations
    // would be orphaned). Say which invoice(s), instead of surfacing the raw foreign-key error.
    const supplierInvoiceBlock = async (): Promise<string | null> => {
      const { data } = await supabase.from('supplier_invoice_allocations')
        .select('supplier_invoice:supplier_invoices(invoice_no)').eq('job_charge_id', id)
      if (!data || data.length === 0) return null
      const nos = [...new Set((data as unknown as { supplier_invoice: { invoice_no: string } | null }[])
        .map(a => a.supplier_invoice?.invoice_no).filter(Boolean))]
      return `This cost line has been allocated to supplier invoice${nos.length === 1 ? '' : 's'} ${nos.join(', ')}. Remove the allocation first (Accounts -> Cost invoices -> open the invoice -> Remove), then delete the line.`
    }
    const blockedMsg = await supplierInvoiceBlock()
    if (blockedMsg) { setError(blockedMsg); toast(blockedMsg, 'error'); return }
    if (!window.confirm('Delete this charge? This cannot be undone.')) return
    setDeletingId(id)
    setError(null)
    try {
      const { error } = await supabase.from('job_charges').delete().eq('id', id).select().single()
      if (error) {
        // Race: an allocation was added between the check above and the delete.
        if ((error as { code?: string }).code === '23503') throw new Error('This cost line has just been allocated to a supplier invoice -- remove the allocation first.')
        throw error
      }
      setSelectedForInvoice(prev => { const next = new Set(prev); next.delete(id); return next })
      await loadAll()
      toast('Charge deleted.', 'success')
    } catch (err) {
      const message = errorMessage(err, 'Failed to delete charge')
      console.error('[ChargesTab] deleteCharge failed', { id, err })
      Sentry.captureException(err instanceof Error ? err : new Error(message), { extra: { id } })
      setError(message)
      toast(`Failed to delete charge: ${message}`, 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const totals = useMemo(() => {
    let sales = 0, estCost = 0, actCost = 0, profitEst = 0, profitAct = 0
    let costLines = 0, actualizedLines = 0
    for (const c of charges) {
      if (c.include_in_sale) sales += c.sale_amount || 0
      if (c.include_in_cost) {
        costLines++
        estCost += c.est_amount || 0
        actCost += c.act_amount ?? c.est_amount ?? 0
        if (c.act_amount != null) actualizedLines++
      }
      profitEst += c.est_profit || 0
      profitAct += c.act_profit || 0
    }
    return { sales, estCost, actCost, profitEst, profitAct, costLines, actualizedLines }
  }, [charges])

  // Group by invoice for display, matching Gary's spec -- one flat sticky-scroll table with a
  // full-width header row per group, rather than N separate tables (simpler to keep exactly one
  // scroll-sync instance, still reads as grouped sections).
  const groupedRows = useMemo(() => {
    const byInvoice = new Map<string, { invoice: JobCharge['invoice']; rows: JobCharge[] }>()
    const uninvoiced: JobCharge[] = []
    for (const c of charges) {
      if (c.invoice_id && c.invoice) {
        if (!byInvoice.has(c.invoice_id)) byInvoice.set(c.invoice_id, { invoice: c.invoice, rows: [] })
        byInvoice.get(c.invoice_id)!.rows.push(c)
      } else {
        uninvoiced.push(c)
      }
    }
    return { invoiced: Array.from(byInvoice.values()), uninvoiced }
  }, [charges])

  const toggleSelectForInvoice = (id: string) => {
    setSelectedForInvoice(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const openCreateInvoiceDialog = () => {
    const selected = charges.filter(c => selectedForInvoice.has(c.id))
    if (selected.length === 0) return
    const customerIds = new Set(selected.map(c => c.sale_customer_id).filter(Boolean))
    if (customerIds.size === 0) {
      setInvoiceError('Selected charges have no Bill To customer set.')
      return
    }
    if (customerIds.size > 1) {
      setInvoiceError('All selected charges must have the same Bill To customer -- split the selection and create separate invoices.')
      return
    }
    setInvoiceError(null)
    // Auto-pick the category from the selected charges: any duty/VAT-type code -> Duty & VAT,
    // otherwise Import. Staff can still change the dropdown; the reason is shown under it.
    const dutyHits = [...new Set(selected.map(c => c.charge_code_id).filter((id): id is string => !!id && dutyVatCodes.has(id)).map(id => dutyVatCodes.get(id)!))]
    setInvoiceCategoryChoice(dutyHits.length > 0 ? 'DUTY_VAT' : 'IMPORT')
    setCategoryAutoReason(dutyHits.length > 0 ? `Auto: contains ${dutyHits.join(', ')}` : 'Auto: no duty/VAT charges selected')
    setInvoiceNotesInput('')
    setShowInvoiceTypeDialog(true)
  }

  const handleCreateInvoice = async () => {
    const selected = charges.filter(c => selectedForInvoice.has(c.id))
    const invoiceCategory = invoiceCategoryChoice

    setCreatingInvoice(true)
    setInvoiceError(null)
    try {
      const { data: baseInvoiceNo, error: noErr } = await supabase.rpc('generate_invoice_no')
      if (noErr || !baseInvoiceNo) throw noErr || new Error('Failed to generate invoice number')
      // D suffix on the same shared sequence, not a separate counter -- see the
      // generate_invoice_no() migration comment (K8766D and K8767 are adjacent numbers in Sage's
      // real data, one number issued once, in either form).
      const invoiceNo = isDutyVat(invoiceCategory) ? `${baseInvoiceNo}D` : baseInvoiceNo

      const subtotal = selected.reduce((s, c) => s + (c.sale_amount || 0), 0)
      const vatAmount = selected.reduce((s, c) => s + ((c.sale_amount || 0) * (c.sale_vat_rate || 0) / 100), 0)
      const { data: { user } } = await supabase.auth.getUser()
      const invoiceDate = new Date().toISOString().slice(0, 10)

      const { data: customerRow } = await supabase.from('customers').select('payment_terms').eq('id', selected[0].sale_customer_id).maybeSingle()
      const dueDate = computeInvoiceDueDate(invoiceDate, invoiceCategory, customerRow?.payment_terms)

      const { data: invoice, error: invErr } = await supabase
        .from('invoices')
        .insert({
          invoice_no: invoiceNo,
          // Document kind vs invoice kind are separate columns: invoice_type is constrained to
          // INVOICE/CREDIT_NOTE (writing 'Import Invoice' there fails invoices_invoice_type_check).
          invoice_type: 'INVOICE',
          invoice_category: invoiceCategory,
          customer_id: selected[0].sale_customer_id,
          job_id: jobId,
          invoice_date: invoiceDate,
          due_date: dueDate,
          notes: invoiceNotesInput.trim() || null,
          currency: selected[0].sale_currency || 'GBP',
          amount: subtotal,
          vat_amount: vatAmount,
          total_amount: subtotal + vatAmount,
          status: 'ISSUED',
          created_by: user?.email || 'unknown',
        })
        .select('id')
        .single()
      if (invErr || !invoice) throw invErr || new Error('Failed to create invoice')

      const { data: linked, error: linkErr } = await supabase
        .from('job_charges')
        .update({ invoice_id: invoice.id })
        .in('id', selected.map(c => c.id))
        .select('id')
      if (linkErr) throw linkErr
      if (!linked || linked.length !== selected.length) {
        throw new Error(`Invoice ${invoiceNo} was created, but only ${linked?.length ?? 0}/${selected.length} charges were linked to it -- check job_charges manually before relying on this invoice's totals.`)
      }

      setSelectedForInvoice(new Set())
      setShowInvoiceTypeDialog(false)
      await loadAll()
      toast(`Invoice ${invoiceNo} created.`, 'success')
    } catch (err) {
      const message = errorMessage(err, 'Failed to create invoice')
      console.error('[ChargesTab] handleCreateInvoice failed', { jobId, selected: Array.from(selectedForInvoice), err })
      Sentry.captureException(err instanceof Error ? err : new Error(message), { extra: { jobId, selected: Array.from(selectedForInvoice) } })
      setInvoiceError(message)
      toast(`Failed to create invoice: ${message}`, 'error')
    } finally {
      setCreatingInvoice(false)
    }
  }

  const { scrollProps, contentRef, bar } = useStickyHScroll([charges])

  const chargeLabel = (c: JobCharge) => {
    const code = chargeCodes.find(cc => cc.id === c.charge_code_id)
    return code?.code || '(no code)'
  }
  const chargeDescription = (c: JobCharge) => {
    const code = chargeCodes.find(cc => cc.id === c.charge_code_id)
    return code?.description || c.description || ''
  }

  // Renders the VAT Rate picker for either side (Sales/Cost) -- three real states, not just
  // "empty means still loading" (that conflation is exactly what left this stuck on "Loading…"
  // forever for anyone whose read got RLS-filtered to zero rows, no error, nothing to retry).
  const renderTaxRateField = (value: string, onSelect: (code: string, rate: number | null) => void) => {
    if (taxRatesStatus === 'error') {
      return (
        <div className="space-y-1">
          <input
            type="text"
            value={value}
            onChange={e => onSelect(e.target.value, null)}
            placeholder="Tax rate name (manual)"
            className="w-full px-2 py-1.5 border border-red-300 dark:border-red-700 rounded dark:bg-gray-700 dark:text-white"
          />
          <button type="button" onClick={loadTaxRates} className="text-xs text-red-600 dark:text-red-400 hover:underline">
            加载税率失败, 点击重试
          </button>
        </div>
      )
    }
    return (
      <select
        value={value}
        disabled={taxRatesStatus === 'loading'}
        onChange={e => {
          const rate = taxRates.find(r => r.name === e.target.value)
          onSelect(e.target.value, rate ? rate.rate : null)
        }}
        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white disabled:opacity-60"
      >
        {taxRatesStatus === 'loading' && <option value="">Loading…</option>}
        {taxRatesStatus === 'ready' && taxRates.length === 0 && <option value="">No tax rates configured</option>}
        {taxRates.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
      </select>
    )
  }

  if (loading) return <div className="text-center py-8">Loading charges...</div>

  const salesLocked = !!form.invoice_id

  const renderRow = (c: JobCharge, inUninvoicedGroup: boolean) => (
    <tr
      key={c.id}
      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${(c.est_profit ?? 0) < 0 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
    >
      {inUninvoicedGroup && (
        <td className="px-2 py-2">
          <input type="checkbox" checked={selectedForInvoice.has(c.id)} onChange={() => toggleSelectForInvoice(c.id)} />
        </td>
      )}
      {!inUninvoicedGroup && <td className="px-2 py-2" />}
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300 font-medium">{chargeLabel(c)}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{chargeDescription(c)}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400 max-w-[160px] truncate" title={c.notes || undefined}>{c.notes || '—'}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.sale_customer?.name || '—'}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-900 dark:text-white font-medium">
        {isInvoiced(c) && <span className="mr-1" title={`Invoiced -- ${c.invoice?.invoice_no || ''}`}>🔒</span>}
        {fmt(c.sale_amount)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.sale_currency}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.sale_vat_rate ?? 0}%</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.cost_supplier?.name || (c.include_in_cost
        ? <span className="text-xs text-gray-400 italic" title="Cost line has no supplier yet -- fill it in when known">Supplier TBC</span>
        : '—')}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmt(c.est_amount)}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.act_amount == null ? '—' : fmt(c.act_amount)}{invoicedChargeIds.has(c.id) && <span className="ml-1 text-xs" title="Actual cost comes from supplier invoice allocations">📄</span>}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.supplier_ref || '—'}</td>
      <td className={`px-3 py-2 whitespace-nowrap font-medium ${(c.est_profit ?? 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
        {fmt(c.est_profit)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex gap-2">
          <button onClick={() => startEdit(c)} className="text-blue-600 hover:text-blue-800 dark:hover:text-blue-400 text-xs font-medium">Edit</button>
          <button
            onClick={() => deleteCharge(c.id)}
            disabled={deletingId === c.id || isInvoiced(c)}
            title={isInvoiced(c) ? `Already invoiced -- ${c.invoice?.invoice_no || ''}. Void the invoice before deleting.` : undefined}
            className="text-red-600 hover:text-red-800 dark:hover:text-red-400 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-600"
          >
            {deletingId === c.id ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </td>
    </tr>
  )

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500 dark:text-gray-400 italic">Charges save immediately -- Add/Edit/Delete here doesn't wait for the job's own Save button.</p>

      {/* Backed up by toast(..., 'error')'s native alert() (see useToast) so a save failure is
          never only this banner -- Gary's report was that a failed save gave no visible signal
          at all; this is the belt, the alert is the suspenders. */}
      {error && (
        <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-700 text-red-800 dark:text-red-300 text-sm font-medium rounded-lg p-3">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Sales</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">£{fmt(totals.sales)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Est Cost</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">£{fmt(totals.estCost)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Act Cost</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">£{fmt(totals.actCost)}</p>
          {totals.costLines > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400">({totals.actualizedLines}/{totals.costLines} lines actualized)</p>
          )}
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Job Profit (Est)</p>
          <p className={`text-xl font-bold ${totals.profitEst < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>£{fmt(totals.profitEst)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Job Profit (Act)</p>
          <p className={`text-xl font-bold ${totals.profitAct < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>£{fmt(totals.profitAct)}</p>
        </div>
      </div>

      <BillingBasisPanel jobId={jobId} />

      {/* Charges list, grouped */}
      {charges.length > 0 && (
        <div className="space-y-2">
          {groupedRows.uninvoiced.length > 0 && selectedForInvoice.size > 0 && (
            <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <span className="text-sm text-blue-800 dark:text-blue-300">{selectedForInvoice.size} charge(s) selected</span>
              <button
                onClick={openCreateInvoiceDialog}
                disabled={creatingInvoice}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {creatingInvoice ? 'Creating…' : 'Create Invoice'}
              </button>
            </div>
          )}
          {invoiceError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg p-3">{invoiceError}</div>
          )}

          <div className="overflow-x-auto" {...scrollProps}>
            <table ref={contentRef as React.RefObject<HTMLTableElement>} className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-2 py-2 w-8" />
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Charge</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Remarks</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">CustName</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">InvAmnt</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Crncy</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">VAT%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">SupplierName</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">EstCost</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">ActCost</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">SupplRef</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">EstProfit</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {groupedRows.invoiced.map(g => (
                  <Fragment key={g.invoice!.id}>
                    <tr className="bg-gray-100 dark:bg-gray-800">
                      <td colSpan={14} className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        ▼ {g.rows[0]?.sale_customer?.name || 'Unknown'} · Invoice {g.invoice!.invoice_no} · {g.invoice!.status}
                        {' · '}
                        <button onClick={() => openInvoice(g.invoice!.id)} className="text-blue-600 hover:underline">View Invoice</button>
                      </td>
                    </tr>
                    {g.rows.map(c => renderRow(c, false))}
                  </Fragment>
                ))}
                {groupedRows.uninvoiced.length > 0 && (
                  <Fragment key="uninvoiced">
                    <tr className="bg-amber-50 dark:bg-amber-900/20">
                      <td colSpan={14} className="px-3 py-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
                        ▼ Uninvoiced ⚠️ {groupedRows.uninvoiced.length}
                      </td>
                    </tr>
                    {groupedRows.uninvoiced.map(c => renderRow(c, true))}
                  </Fragment>
                )}
              </tbody>
            </table>
          </div>
          <StickyHScrollbar {...bar} />
        </div>
      )}

      {/* Add / Edit Charge */}
      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900 dark:text-white">{editingId ? 'Edit Charge' : 'Add Charge'}</h3>
          {editingId && (
            <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel edit</button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Charge Code</label>
          <SearchableSelect
            options={chargeCodes.map(c => ({ id: c.id, label: `${c.code} - ${c.description}` }))}
            value={form.charge_code_id || ''}
            onChange={(value) => {
              setForm({ ...form, charge_code_id: value })
              setFieldErrors(prev => { const next = new Set(prev); next.delete('charge_code_id'); return next })
            }}
            placeholder="Search charge code..."
            onCreate={createChargeCode}
            invalid={fieldErrors.has('charge_code_id')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sales column -- read-only once invoiced; the invoice is a committed customer promise,
              changing it here would silently desync from what was actually issued/sent. */}
          <div className="space-y-3 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sales</h4>
              {!salesLocked && (
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                  <input type="checkbox" checked={form.include_in_sale ?? true} onChange={e => {
                    setForm({ ...form, include_in_sale: e.target.checked })
                    if (!e.target.checked) setFieldErrors(prev => { const next = new Set(prev); next.delete('sale_customer_id'); return next })
                  }} />
                  Include in Sale
                </label>
              )}
            </div>
            {salesLocked ? (
              <div className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
                <div><span className="text-gray-500 dark:text-gray-400">Bill To:</span> {form.sale_customer?.name || '—'}</div>
                <div><span className="text-gray-500 dark:text-gray-400">Qty × Rate:</span> {form.sale_quantity ?? '—'} × {form.sale_rate ?? '—'}</div>
                <div><span className="text-gray-500 dark:text-gray-400">Currency:</span> {form.sale_currency} (exch {form.sale_exch_rate ?? 1})</div>
                <div><span className="text-gray-500 dark:text-gray-400">VAT Rate:</span> {form.sales_tax_code || '—'} ({form.sale_vat_rate ?? 0}%)</div>
                {form.notes && <div><span className="text-gray-500 dark:text-gray-400">Remarks:</span> {form.notes}</div>}
                <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded p-2 mt-2">
                  ℹ️ Already invoiced -- {form.invoice?.invoice_no}. To change amounts, Void the invoice and re-issue.
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bill To</label>
                  {/* Server-side search: customers is 5,000+ rows; the old pre-loaded list silently held only the first 500. */}
                  <CustomerCombobox
                    anyRole
                    value={form.sale_customer_id || ''}
                    onChange={v => {
                      setForm({ ...form, sale_customer_id: v })
                      setFieldErrors(prev => { const next = new Set(prev); next.delete('sale_customer_id'); return next })
                    }}
                    placeholder="Search company…"
                    invalid={fieldErrors.has('sale_customer_id')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Qty</label>
                    <input type="number" step="0.01" value={form.sale_quantity ?? ''} onChange={e => setForm({ ...form, sale_quantity: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rate</label>
                    <input type="number" step="0.01" value={form.sale_rate ?? ''} onChange={e => setForm({ ...form, sale_rate: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Currency</label>
                    <SearchableSelect
                      options={[{ id: 'GBP', label: 'GBP £' }, { id: 'USD', label: 'USD $' }, { id: 'EUR', label: 'EUR €' }, { id: 'CNY', label: 'CNY ¥' }]}
                      value={form.sale_currency || 'GBP'}
                      onChange={v => setForm({ ...form, sale_currency: v })}
                      placeholder="Currency..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Exch Rate → GBP</label>
                    <input type="number" step="0.0001" value={form.sale_exch_rate ?? 1} onChange={e => setForm({ ...form, sale_exch_rate: e.target.value ? parseFloat(e.target.value) : 1 })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VAT Rate</label>
                    {renderTaxRateField(form.sales_tax_code || '', (code, rate) =>
                      setForm({ ...form, sales_tax_code: code, sale_vat_rate: rate ?? form.sale_vat_rate }))}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VAT %</label>
                    <input type="number" step="0.01" value={form.sale_vat_rate ?? ''} onChange={e => setForm({ ...form, sale_vat_rate: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-800 dark:text-white" title="Auto-filled from VAT Rate -- override here for a special case" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Remarks</label>
                  <input type="text" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Prints on the invoice line, e.g. HBL:4/CODE:63"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                </div>
              </>
            )}
          </div>

          {/* Cost column */}
          <div className="space-y-3 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Cost</h4>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                <input type="checkbox" checked={form.include_in_cost ?? true} onChange={e => {
                  setForm({ ...form, include_in_cost: e.target.checked })
                }} />
                Include in Cost
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Supplier</label>
              {/* Z86: server-side search (customers is 5,000+ rows -- the old pre-loaded list only held 500). */}
              <CustomerCombobox
                anyRole
                value={form.cost_supplier_id || ''}
                onChange={v => setForm({ ...form, cost_supplier_id: v })}
                placeholder="Search company…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Qty</label>
                <input type="number" step="0.01" value={form.cost_quantity ?? ''} onChange={e => setForm({ ...form, cost_quantity: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Est Rate</label>
                <input type="number" step="0.01" value={form.cost_rate ?? ''} onChange={e => setForm({ ...form, cost_rate: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Act Cost (once supplier invoice received)</label>
              <input type="number" step="0.01" value={form.act_amount ?? ''} readOnly={!!form.id && invoicedChargeIds.has(form.id)} title={form.id && invoicedChargeIds.has(form.id) ? 'Calculated from supplier invoice allocations (Accounts -> Cost invoices)' : undefined} onChange={e => setForm({ ...form, act_amount: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white read-only:bg-gray-100 dark:read-only:bg-gray-800" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Currency</label>
                <SearchableSelect
                  options={[{ id: 'GBP', label: 'GBP £' }, { id: 'USD', label: 'USD $' }, { id: 'EUR', label: 'EUR €' }, { id: 'CNY', label: 'CNY ¥' }]}
                  value={form.cost_currency || 'GBP'}
                  onChange={v => setForm({ ...form, cost_currency: v })}
                  placeholder="Currency..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Exch Rate → GBP</label>
                <input type="number" step="0.0001" value={form.cost_exch_rate ?? 1} onChange={e => setForm({ ...form, cost_exch_rate: e.target.value ? parseFloat(e.target.value) : 1 })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VAT Rate</label>
                {renderTaxRateField(form.cost_tax_code || '', (code, rate) =>
                  setForm({ ...form, cost_tax_code: code, cost_vat_rate: rate ?? form.cost_vat_rate }))}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VAT %</label>
                <input type="number" step="0.01" value={form.cost_vat_rate ?? ''} onChange={e => setForm({ ...form, cost_vat_rate: e.target.value ? parseFloat(e.target.value) : null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-800 dark:text-white" title="Auto-filled from VAT Rate -- override here for a special case" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Supplier Ref</label>
                <input type="text" value={form.supplier_ref || ''} onChange={e => setForm({ ...form, supplier_ref: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Supplier Invoice Received</label>
                <input type="date" value={form.supplier_invoice_received_at ? String(form.supplier_invoice_received_at).slice(0, 10) : ''} onChange={e => setForm({ ...form, supplier_invoice_received_at: e.target.value || null })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={saveCharge}
          disabled={saving}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Charge'}
        </button>
      </div>

      {viewingInvoice && (
        <InvoiceDetailModal
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onStatusChange={handleInvoiceStatusChange}
          onNotesChange={handleInvoiceNotesChange}
          onChanged={loadAll}
        />
      )}

      {showInvoiceTypeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Create Invoice</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Invoice Category</label>
                <select
                  value={invoiceCategoryChoice}
                  onChange={e => { setInvoiceCategoryChoice(e.target.value as InvoiceCategory); setCategoryAutoReason(null) }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                >
                  {INVOICE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {categoryAutoReason && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{categoryAutoReason}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isDutyVat(invoiceCategoryChoice)
                    ? 'Due immediately (same day as invoice date). Invoice number gets a "D" suffix.'
                    : 'Due date follows the customer\'s own payment terms (defaults to 30 days).'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes (optional)</label>
                <textarea rows={2} value={invoiceNotesInput} onChange={e => setInvoiceNotesInput(e.target.value)}
                  placeholder="Descriptive text only -- never affects amounts. Editable later too."
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
              </div>
              {invoiceError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg p-3">{invoiceError}</div>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
              <button onClick={() => setShowInvoiceTypeDialog(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button onClick={handleCreateInvoice} disabled={creatingInvoice}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {creatingInvoice ? 'Creating…' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
